/**
 * BURST — confetti cannons, poppers and fireworks.
 *
 * The family that FALL cannot express, because every particle here shares an
 * ORIGIN and a birth TIME rather than being spread across the viewport. A burst
 * that spawns its particles independently reads as drizzle, not as an explosion.
 *
 * Three things make it read as a real burst, and all three are commonly skipped:
 *
 *   1. DRAG. Real confetti decelerates hard in the first ~200 ms and then
 *      flutters down. Constant velocity plus gravity gives a mortar shell arc,
 *      which is unmistakably wrong for paper.
 *   2. A LIFETIME with a fade. Particles that simply leave the viewport leave a
 *      burst hanging in the air for seconds after it should be over.
 *   3. FLUTTER — a per-particle oscillation that grows as the particle slows, so
 *      the fast opening is clean and the slow tail is chaotic. A flutter applied
 *      at constant amplitude makes the launch look wobbly.
 */

import { clamp } from '../budget.ts';
import type { Family, Particle, StepContext } from '../types.ts';

export interface BurstOptions {
  depth: 1 | 2 | 3;
  baseSize: number;
  /** Launch speed in CSS px/s, before the per-particle spread. */
  speed: number;
  /** Downward acceleration, CSS px/s². */
  gravity: number;
  /** Velocity retained per second, 0..1. 0.1 means a hard stop. */
  drag: number;
  /** Seconds a particle lives before it has fully faded. */
  life: number;
  /** Flutter amplitude in CSS px/s at full slowdown. */
  flutter: number;
  /** Tumble, radians/second. */
  spin: number;
  /** Where the burst originates, as viewport fractions. */
  origin: { x: number; y: number };
  /** Half-angle of the launch cone, radians. `Math.PI` is a full circle. */
  spread: number;
  /** Direction the cone points, radians. -PI/2 is straight up. */
  aim: number;
  /** Seconds between automatic re-bursts. 0 means one burst, on demand. */
  interval: number;
  cellCount: number;
  tintCount: number;
  sizeScale: number;
}

const BAND_SIZE = [0.7, 0.9, 1.15] as const;

export function createBurstFamily(opts: BurstOptions): Family {
  const bands = Math.max(1, opts.depth);

  /**
   * How long the particle about to be spawned must wait before it launches.
   *
   * Set by `step` immediately before it returns false, and consumed by the
   * `spawn` the loop calls synchronously on the next line. That coupling is the
   * whole mechanism, so it is stated rather than implied.
   *
   * ⚠ The obvious implementation — a shared `volleyAt` that each dying particle
   * checks and updates — is WRONG, and wrong in a way that looks right. The
   * first particle past the interval updates the shared timestamp, so every
   * other particle dying in that same frame then sees "no time has passed" and
   * goes back to waiting. Exactly ONE particle relaunches per volley, and the
   * effect renders as a nearly-empty canvas with an occasional speck. Measured:
   * 0–20 lit pixels on a 260x120 tile where a firework should fill it.
   *
   * Deriving the wait from the clock instead means every particle that dies
   * inside one interval computes the SAME wake time, so they relaunch together —
   * which is what makes it a volley rather than a trickle.
   */
  let nextWait = 0;

  return {
    id: 'burst',
    countScale: 1,

    spawn(p: Particle, ctx: StepContext, rnd: () => number) {
      const band = (rnd() * bands) | 0;
      p.depth = band;
      p.cell = (rnd() * opts.cellCount) | 0;
      p.tint = (rnd() * opts.tintCount) | 0;
      p.size = opts.baseSize * BAND_SIZE[band] * (0.75 + rnd() * 0.5) * opts.sizeScale;

      p.x = ctx.w * opts.origin.x;
      p.y = ctx.h * opts.origin.y;

      // Speed varies per particle, and the SQUARE ROOT of a uniform draw fills
      // the cone evenly. A uniform radius crowds the centre and produces a
      // visible dense core that real confetti does not have.
      const angle = opts.aim + (rnd() * 2 - 1) * opts.spread;
      const mag = opts.speed * (0.45 + Math.sqrt(rnd()) * 0.75);
      p.vx = Math.cos(angle) * mag;
      p.vy = Math.sin(angle) * mag;

      p.r = rnd() * Math.PI * 2;
      p.vr = (rnd() - 0.5) * 2 * opts.spin;
      p.alpha = 1;
      // Negative age = not yet launched. The volley wait (if any) plus a small
      // stagger, so the volley has a leading edge instead of a single hard-edged
      // ring.
      p.age = -nextWait - rnd() * 0.06;
      p.phase = rnd() * Math.PI * 2;
    },

    step(p: Particle, ctx: StepContext): boolean {
      p.age += ctx.dt;
      if (p.age < 0) {
        // Not yet launched. Hold it at the origin, invisible.
        p.alpha = 0;
        return true;
      }

      // Exponential drag, framerate-independent. Multiplying by a constant per
      // frame instead would make the burst travel further on a fast machine.
      const keep = Math.pow(opts.drag, ctx.dt);
      p.vx *= keep;
      p.vy *= keep;
      p.vy += opts.gravity * ctx.dt;

      // Flutter grows as the particle slows: clean launch, chaotic tail.
      const slowness = 1 - clamp(Math.abs(p.vy) / (opts.speed || 1), 0, 1);
      const flut = opts.flutter * slowness * Math.sin(p.phase + p.age * 7);

      p.x += (p.vx + flut + ctx.wind * 70) * ctx.dt;
      p.y += p.vy * ctx.speed * ctx.dt;
      p.r += p.vr * ctx.dt;

      // Fade over the last third of the life, so a burst ends rather than
      // simply leaving.
      const k = p.age / opts.life;
      p.alpha = k > 0.67 ? clamp((1 - k) * 3, 0, 1) : 1;

      if (k >= 1 || p.y > ctx.h + p.size * 2) {
        // Time from now until the next volley boundary. Derived from the clock,
        // so every particle dying inside one interval agrees on it.
        nextWait = opts.interval > 0 ? opts.interval - (ctx.t % opts.interval) : 0;
        return false; // recycle → the loop calls spawn() on the very next line
      }
      return true;
    },
  };
}

export const BURST_PRESETS = {
  /** Two cannons at the lower corners, aimed inward and up. */
  cannon: {
    baseSize: 13,
    speed: 900,
    gravity: 620,
    drag: 0.14,
    life: 3.2,
    flutter: 130,
    spin: 7,
    origin: { x: 0.5, y: 1.02 },
    spread: 0.42,
    aim: -Math.PI / 2,
    interval: 0,
  },
  /** A popper at the centre — shorter, wider, gentler. */
  popper: {
    baseSize: 11,
    speed: 620,
    gravity: 540,
    drag: 0.2,
    life: 2.6,
    flutter: 110,
    spin: 6,
    origin: { x: 0.5, y: 0.62 },
    spread: Math.PI,
    aim: 0,
    interval: 0,
  },
  /**
   * A firework shell: full circle, low gravity, long fade, repeating.
   *
   * ⚠ `interval` must be a little LONGER than `life`, never shorter. Particles
   * all share a lifetime, so they all die at once and then wait for the next
   * volley boundary — an interval shorter than the life does not overlap
   * volleys, it just quantises the wait upward. An interval of 1.6 against a
   * life of 2.4 produced a canvas that was empty most of the time. Slightly over
   * the lifetime gives a short, deliberate beat between shells.
   */
  firework: {
    baseSize: 8,
    speed: 520,
    gravity: 190,
    drag: 0.35,
    life: 2.4,
    flutter: 30,
    spin: 2,
    origin: { x: 0.5, y: 0.32 },
    spread: Math.PI,
    aim: 0,
    interval: 2.6,
  },
  /** Sparkle pop — small, fast, short-lived, repeating quickly. */
  sparkle: {
    baseSize: 7,
    speed: 380,
    gravity: 60,
    drag: 0.25,
    life: 1.1,
    flutter: 20,
    spin: 3,
    origin: { x: 0.5, y: 0.5 },
    spread: Math.PI,
    aim: 0,
    interval: 1.25,
  },
  /** Coins from a treasure pop — heavy, fast fall, little flutter. */
  coins: {
    baseSize: 18,
    speed: 700,
    gravity: 1100,
    drag: 0.3,
    life: 2.6,
    flutter: 40,
    spin: 8,
    origin: { x: 0.5, y: 0.85 },
    spread: 0.7,
    aim: -Math.PI / 2,
    interval: 0,
  },
} as const satisfies Record<
  string,
  Omit<BurstOptions, 'depth' | 'cellCount' | 'tintCount' | 'sizeScale'>
>;

export type BurstPresetId = keyof typeof BURST_PRESETS;

export function burstOptions(
  preset: BurstPresetId,
  o: Pick<BurstOptions, 'depth' | 'cellCount' | 'tintCount' | 'sizeScale'>
): BurstOptions {
  return { ...BURST_PRESETS[preset], ...o };
}
